import { getBoardToolLabel } from "./agent-tool-labels";
import {
	isGenerateImageInChatOutput,
	summarizeGenerateImageInChatOutput,
} from "./generate-image-in-chat";
import { isPresentOptionsOutput } from "./present-options";
import type { AgentChatAssistantPart } from "./types";

export type AgentChatStreamPart =
	| {
			type: "text-start" | "text-end";
			id: string;
	  }
	| {
			type: "text-delta" | "reasoning-delta";
			id: string;
			text: string;
	  }
	| {
			type: "reasoning-start" | "reasoning-end";
			id: string;
	  }
	| {
			type: "tool-input-start";
			id: string;
			toolName: string;
	  }
	| {
			type: "tool-input-delta";
			id: string;
			delta: string;
	  }
	| {
			type: "tool-call";
			toolCallId: string;
			toolName: string;
			input?: unknown;
	  }
	| {
			type: "tool-result";
			toolName: string;
			toolCallId?: string;
			output?: unknown;
	  }
	| {
			type: "tool-error" | "tool-output-denied";
			toolName: string;
			toolCallId?: string;
	  }
	| {
			type: "tool-approval-request";
			approvalId: string;
			toolCall: {
				toolCallId: string;
				toolName: string;
				input?: unknown;
			};
			isAutomatic?: boolean;
	  }
	| {
			type: "error";
			error: unknown;
	  };

function parseJsonIfPossible(value: string): unknown {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

export type AgentChatStreamParserState = {
	assistantParts: AgentChatAssistantPart[];
	textPartIndex: number | null;
	reasoningPartIndexes: Map<string, number>;
	reasoningStartedAt: Map<string, number>;
	toolInputIndexes: Map<string, number>;
	toolInputBuffers: Map<string, string>;
};

export function createAgentChatStreamParserState(): AgentChatStreamParserState {
	return {
		assistantParts: [],
		textPartIndex: null,
		reasoningPartIndexes: new Map(),
		reasoningStartedAt: new Map(),
		toolInputIndexes: new Map(),
		toolInputBuffers: new Map(),
	};
}

export function processAgentChatStreamPart(
	state: AgentChatStreamParserState,
	streamPart: AgentChatStreamPart,
): boolean {
	let shouldSync = false;

	switch (streamPart.type) {
		case "text-start": {
			const currentTextPartIndex = state.textPartIndex;
			const shouldStartNewTextPart =
				currentTextPartIndex === null ||
				state.assistantParts.some(
					(part, index) =>
						index > currentTextPartIndex && part.type === "tool-result",
				);

			if (shouldStartNewTextPart) {
				state.textPartIndex =
					state.assistantParts.push({
						type: "text",
						text: "",
					}) - 1;
				shouldSync = true;
			}
			break;
		}
		case "text-delta": {
			if (state.textPartIndex === null) {
				state.textPartIndex =
					state.assistantParts.push({
						type: "text",
						text: "",
					}) - 1;
			}

			const currentTextPart = state.assistantParts[state.textPartIndex];
			if (currentTextPart?.type === "text") {
				currentTextPart.text += streamPart.text;
				shouldSync = true;
			}
			break;
		}
		case "reasoning-start": {
			const partIndex =
				state.assistantParts.push({
					type: "reasoning",
					text: "",
					done: false,
				}) - 1;
			state.reasoningPartIndexes.set(streamPart.id, partIndex);
			state.reasoningStartedAt.set(streamPart.id, Date.now());
			shouldSync = true;
			break;
		}
		case "reasoning-delta": {
			const partIndex = state.reasoningPartIndexes.get(streamPart.id);
			if (partIndex !== undefined) {
				const reasoningPart = state.assistantParts[partIndex];
				if (reasoningPart?.type === "reasoning") {
					reasoningPart.text += streamPart.text;
					shouldSync = true;
				}
			}
			break;
		}
		case "reasoning-end": {
			const partIndex = state.reasoningPartIndexes.get(streamPart.id);
			if (partIndex !== undefined) {
				const reasoningPart = state.assistantParts[partIndex];
				if (reasoningPart?.type === "reasoning") {
					reasoningPart.done = true;
					const startedAt = state.reasoningStartedAt.get(streamPart.id);
					if (startedAt !== undefined) {
						reasoningPart.durationMs = Date.now() - startedAt;
					}
					shouldSync = true;
				}
			}
			break;
		}
		case "tool-input-start": {
			const partIndex =
				state.assistantParts.push({
					type: "tool-call",
					toolCallId: streamPart.id,
					toolName: streamPart.toolName,
					state: "input-streaming",
				}) - 1;
			state.toolInputIndexes.set(streamPart.id, partIndex);
			state.toolInputBuffers.set(streamPart.id, "");
			shouldSync = true;
			break;
		}
		case "tool-input-delta": {
			const partIndex = state.toolInputIndexes.get(streamPart.id);
			if (partIndex !== undefined) {
				const nextBuffer =
					(state.toolInputBuffers.get(streamPart.id) ?? "") + streamPart.delta;
				state.toolInputBuffers.set(streamPart.id, nextBuffer);
				const toolCallPart = state.assistantParts[partIndex];
				if (toolCallPart?.type === "tool-call") {
					toolCallPart.input = parseJsonIfPossible(nextBuffer);
					shouldSync = true;
				}
			}
			break;
		}
		case "tool-call": {
			let partIndex = state.toolInputIndexes.get(streamPart.toolCallId);
			if (partIndex === undefined) {
				partIndex =
					state.assistantParts.push({
						type: "tool-call",
						toolCallId: streamPart.toolCallId,
						toolName: streamPart.toolName,
						state: "running",
						input: streamPart.input,
					}) - 1;
				state.toolInputIndexes.set(streamPart.toolCallId, partIndex);
				shouldSync = true;
				break;
			}

			const toolCallPart = state.assistantParts[partIndex];
			if (toolCallPart?.type === "tool-call") {
				toolCallPart.toolCallId = streamPart.toolCallId;
				toolCallPart.state = "running";
				toolCallPart.input = streamPart.input;
				shouldSync = true;
			}
			break;
		}
		case "tool-result": {
			const matchedToolCallId = completeRunningToolCall(
				state.assistantParts,
				streamPart.toolName,
				streamPart.toolCallId,
			);

			if (
				streamPart.toolName === "presentOptions" &&
				isPresentOptionsOutput(streamPart.output)
			) {
				state.assistantParts.push({
					type: "choice-request",
					choiceId: streamPart.output.choiceId,
					prompt: streamPart.output.prompt,
					options: streamPart.output.options,
				});
			} else if (
				streamPart.toolName === "generateImageInChat" &&
				isGenerateImageInChatOutput(streamPart.output)
			) {
				state.assistantParts.push({
					type: "tool-result",
					toolCallId: matchedToolCallId ?? streamPart.toolCallId,
					toolName: streamPart.toolName,
					output: summarizeGenerateImageInChatOutput(streamPart.output),
				});
				for (const result of streamPart.output.results) {
					if (result.status === "ok") {
						state.assistantParts.push({
							type: "generated-image",
							modelId: result.modelId,
							modelLabel: result.displayName,
							mediaType: result.mediaType,
							data: result.data,
							dataKind: result.dataKind,
							status: "ready",
						});
					} else {
						state.assistantParts.push({
							type: "generated-image",
							modelId: result.modelId,
							modelLabel: result.displayName,
							mediaType: "image/png",
							data: "",
							dataKind: "base64",
							status: "error",
							errorMessage: result.errorMessage,
						});
					}
				}
			} else {
				state.assistantParts.push({
					type: "tool-result",
					toolCallId: matchedToolCallId ?? streamPart.toolCallId,
					toolName: streamPart.toolName,
					output: streamPart.output,
				});
			}
			shouldSync = true;
			break;
		}
		case "tool-error":
		case "tool-output-denied": {
			completeRunningToolCall(
				state.assistantParts,
				streamPart.toolName,
				streamPart.toolCallId,
			);
			state.assistantParts.push({
				type: "action-receipt",
				status: streamPart.type === "tool-error" ? "error" : "denied",
				title: getBoardToolLabel(streamPart.toolName),
				description:
					streamPart.type === "tool-error"
						? "Tool execution failed"
						: "Tool execution denied",
				toolCallId: streamPart.toolCallId,
				toolName: streamPart.toolName,
			});
			shouldSync = true;
			break;
		}
		case "tool-approval-request": {
			if (streamPart.isAutomatic) {
				break;
			}

			const toolCallId = streamPart.toolCall.toolCallId;
			const toolName = streamPart.toolCall.toolName;
			markToolCallAwaitingApproval(
				state.assistantParts,
				toolName,
				toolCallId,
				streamPart.toolCall.input,
			);
			state.assistantParts.push({
				type: "approval-request",
				approvalId: streamPart.approvalId,
				toolCallId,
				toolName,
				input: streamPart.toolCall.input,
			});
			shouldSync = true;
			break;
		}
		case "error":
			break;
		default:
			break;
	}

	return shouldSync;
}

function markToolCallAwaitingApproval(
	parts: AgentChatAssistantPart[],
	toolName: string,
	toolCallId: string,
	input?: unknown,
) {
	for (let index = parts.length - 1; index >= 0; index -= 1) {
		const part = parts[index];
		if (part?.type !== "tool-call") {
			continue;
		}

		if (part.toolCallId && part.toolCallId !== toolCallId) {
			continue;
		}

		if (!part.toolCallId && part.toolName !== toolName) {
			continue;
		}

		part.toolCallId = toolCallId;
		part.toolName = toolName;
		part.state = "awaiting_approval";
		if (input !== undefined) {
			part.input = input;
		}
		return;
	}

	parts.push({
		type: "tool-call",
		toolCallId,
		toolName,
		state: "awaiting_approval",
		input,
	});
}

function completeRunningToolCall(
	parts: AgentChatAssistantPart[],
	toolName: string,
	toolCallId?: string,
): string | undefined {
	for (let index = parts.length - 1; index >= 0; index -= 1) {
		const part = parts[index];
		if (
			part?.type !== "tool-call" ||
			(part.state !== "running" && part.state !== "awaiting_approval")
		) {
			continue;
		}

		if (toolCallId && part.toolCallId && part.toolCallId !== toolCallId) {
			continue;
		}

		if (!toolCallId && part.toolName !== toolName) {
			continue;
		}

		part.state = "completed";
		return part.toolCallId ?? toolCallId;
	}

	return toolCallId;
}
